import { useState, useEffect } from 'react';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Button,
    Grid,
    TextField,
    Typography,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import { listDiretorio, getEquipamentos, downloadArquivo, downloadZip } from 'services/diretorio';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import FilterListIcon from '@mui/icons-material/FilterList';
import { gridSpacing } from 'store/constant';
import { MENU_OPEN } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';


const Diretorio = ({ ...others }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const idUnit = useSelector((state) => state.user.unit || '');
    const [loading, setLoading] = useState(false);
    const [loadingEquipamentos, setLoadingEquipamentos] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [arquivos, setArquivos] = useState([]);
    const [equipamentoOptions, setEquipamentoOptions] = useState([]);
    const [selectedEquipamentos, setSelectedEquipamentos] = useState([]);
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [selectedArquivos, setSelectedArquivos] = useState([]);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === 'diretorio');
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: 'diretorio' });
        }
    }, [dispatch]);

    useEffect(() => {
        loadEquipamentos();
    }, []);

    const loadEquipamentos = async () => {
        try {
            setLoadingEquipamentos(true);
            const response = await getEquipamentos();
            if (response.data.success) {
                setEquipamentoOptions(response.data.data || []);
            }
        } catch (err) {
            setError('Erro ao carregar equipamentos.');
        } finally {
            setLoadingEquipamentos(false);
        }
    };

    const handleSearch = async () => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            const equipamentoIds = selectedEquipamentos.map(item => item.id || item);
            const response = await listDiretorio(equipamentoIds, dataInicio || null, dataFim || null, idUnit || null);

            if (response.data.success) {
                setArquivos(response.data.data || []);
                setSelectedArquivos([]);
                setSuccess(`${response.data.total} arquivo(s) encontrado(s).`);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.data.error || 'Erro ao buscar arquivos.');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao buscar arquivos. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedArquivos(arquivos.map(arquivo => arquivo.id));
        } else {
            setSelectedArquivos([]);
        }
    };

    const handleSelectArquivo = (arquivoId) => {
        const selectedIndex = selectedArquivos.indexOf(arquivoId);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedArquivos, arquivoId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedArquivos.slice(1));
        } else if (selectedIndex === selectedArquivos.length - 1) {
            newSelected = newSelected.concat(selectedArquivos.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedArquivos.slice(0, selectedIndex),
                selectedArquivos.slice(selectedIndex + 1)
            );
        }

        setSelectedArquivos(newSelected);
    };

    const handleDownloadSingle = async (arquivoId) => {
        try {
            setDownloading(true);
            await downloadArquivo(arquivoId);
            setSuccess('Download iniciado com sucesso.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro ao fazer download do arquivo.');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadZip = async () => {
        if (selectedArquivos.length === 0) {
            setError('Selecione pelo menos um arquivo para download.');
            return;
        }

        try {
            setDownloading(true);
            await downloadZip(selectedArquivos);
            setSuccess(`Download de ${selectedArquivos.length} arquivo(s) iniciado com sucesso.`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro ao fazer download dos arquivos.');
        } finally {
            setDownloading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const isSelected = (arquivoId) => selectedArquivos.indexOf(arquivoId) !== -1;
    const isAllSelected = arquivos.length > 0 && selectedArquivos.length === arquivos.length;
    const isIndeterminate = selectedArquivos.length > 0 && selectedArquivos.length < arquivos.length;

    return (
        <MainCard title="Diretório de Arquivos">
            <Grid container spacing={gridSpacing}>
                {/* Filtros */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <FilterListIcon color="primary" />
                            <Typography variant="h6">Filtros</Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    multiple
                                    options={equipamentoOptions}
                                    getOptionLabel={(option) => option.label || ''}
                                    value={selectedEquipamentos}
                                    onChange={(event, newValue) => {
                                        setSelectedEquipamentos(newValue);
                                    }}
                                    disabled={loadingEquipamentos}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Equipamento"
                                            placeholder="Digite para pesquisar..."
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option.id}
                                                label={option.label}
                                                size="small"
                                            />
                                        ))
                                    }
                                    filterSelectedOptions
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    noOptionsText="Nenhum equipamento encontrado"
                                    loadingText="Carregando equipamentos..."
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Data Início"
                                    type="date"
                                    value={dataInicio}
                                    onChange={(e) => setDataInicio(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Data Fim"
                                    type="date"
                                    value={dataFim}
                                    onChange={(e) => setDataFim(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSearch}
                                    disabled={loading}
                                    sx={{ height: '56px' }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Buscar'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Ações de Download */}
                {selectedArquivos.length > 0 && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography>
                                    {selectedArquivos.length} arquivo(s) selecionado(s)
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FolderZipIcon />}
                                    onClick={handleDownloadZip}
                                    disabled={downloading}
                                >
                                    {downloading ? <CircularProgress size={20} /> : 'Baixar ZIP'}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {/* Tabela de Arquivos */}
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={isIndeterminate}
                                            checked={isAllSelected}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell><strong>OS</strong></TableCell>
                                    <TableCell><strong>Data Abertura</strong></TableCell>
                                    <TableCell><strong>Tipo de OS</strong></TableCell>
                                    <TableCell><strong>Unidade</strong></TableCell>
                                    <TableCell><strong>Arquivo</strong></TableCell>
                                    <TableCell><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {arquivos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">
                                                {loading ? 'Carregando...' : 'Nenhum arquivo encontrado. Use os filtros para buscar.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    arquivos.map((arquivo) => {
                                        const isItemSelected = isSelected(arquivo.id);
                                        return (
                                            <TableRow
                                                key={arquivo.id}
                                                hover
                                                selected={isItemSelected}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                        onChange={() => handleSelectArquivo(arquivo.id)}
                                                    />
                                                </TableCell>
                                                <TableCell>{arquivo.os_id}</TableCell>
                                                <TableCell>{formatDate(arquivo.data_abertura)}</TableCell>
                                                <TableCell>{arquivo.tipo_os_nome || '-'}</TableCell>
                                                <TableCell>{arquivo.unidade_nome || '-'}</TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            maxWidth: '300px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        title={arquivo.nome_arquivo}
                                                    >
                                                        {arquivo.nome_arquivo || 'arquivo_' + arquivo.id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Download">
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => handleDownloadSingle(arquivo.id)}
                                                            disabled={downloading}
                                                            size="small"
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {/* Snackbars */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
                    {success}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </MainCard>
    );
};

export default Diretorio;

